import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate, Link } from "react-router-dom";
import { v4 as uuidv4 } from 'uuid';
import useFileUpload from "../../hooks/useFileUpload";
import useProperties from "../../hooks/useProperties";
import useGroupProperties from "../../hooks/useGroupProperties";
import useCities from "../../hooks/useCities";
import { imageService } from "../../services/imageService";
import "./Admin.scss";
import "./GalleryManager.scss";
import Loader from "../../components/loader-spinner/Loader";

// Custom toast implementation since react-toastify isn't installed
const toast = {
  success: (message) => console.log(`Success: ${message}`),
  error: (message) => console.error(`Error: ${message}`),
  info: (message) => console.log(`Info: ${message}`)
};

const GalleryManager = () => {
  const { itemType, itemId } = useParams();
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState(null);
  const [property, setProperty] = useState(null);
  const [galleryImages, setGalleryImages] = useState([]);
  const [uploadFiles, setUploadFiles] = useState([]);
  const [cityPath, setCityPath] = useState('');
  const [usingDbGallery, setUsingDbGallery] = useState(false);
  const fileInputRef = useRef(null);
  const dropAreaRef = useRef(null);
  
  const { getProperty } = useProperties();
  const { getGroupProperty } = useGroupProperties();
  const { cities, communities } = useCities({ autoFetch: true });
  const { uploadFile, listFiles, deleteFile, getFileUrl } = useFileUpload();

  // Fetch property data and gallery images
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      setError(null);
      
      try {
        let propertyData;
        
        if (itemType === 'property') {
          propertyData = await getProperty(itemId);
          console.log('Fetched property data:', propertyData);
        } else if (itemType === 'groupProperty') {
          propertyData = await getGroupProperty(itemId);
          console.log('Fetched group property data:', propertyData);
        } else {
          throw new Error("Invalid item type");
        }
        
        if (propertyData) {
          setProperty(propertyData);
          
          // Find the city for the property
          if (propertyData.community_id && Array.isArray(communities) && Array.isArray(cities)) {
            // Try to find the city through the property's community
            const community = communities.find(c => c.id === propertyData.community_id);
            if (community) {
              const city = cities.find(c => c.id === community.city_id);
              if (city) {
                setCityPath(city.path);
                console.log('Set city path from community:', city.path);
              }
            }
          } else if (propertyData.city_id && Array.isArray(cities)) {
            // For group properties that have a direct city_id reference
            const city = cities.find(c => c.id === propertyData.city_id);
            if (city) {
              setCityPath(city.path);
              console.log('Set city path from city_id:', city.path);
            }
          } else if (propertyData.path) {
            // Fallback: try to extract city from the property path
            const pathParts = propertyData.path.split('/').filter(part => part.length > 0);
            if (pathParts.length >= 1) {
              const cityName = pathParts[0]; // e.g., "dubai" from "/dubai/community/property"
              setCityPath(cityName);
              console.log('Set city path from property path:', cityName);
            }
          } else if (propertyData.gallery_path) {
            // For group properties that have a gallery_path directly provided
            const pathParts = propertyData.gallery_path.split('/').filter(part => part.length > 0);
            if (pathParts.length >= 1) {
              const cityName = pathParts[0];
              setCityPath(cityName);
              console.log('Set city path from gallery_path:', cityName);
            }
          }

          // Get gallery images from the database
          const dbGalleryImages = await imageService.getGalleryImages(propertyData.id);
          if (dbGalleryImages && dbGalleryImages.length > 0) {
            // Sort images by position
            const sortedImages = [...dbGalleryImages].sort((a, b) => a.position - b.position);
            setGalleryImages(sortedImages);
            setUsingDbGallery(true);
            console.log('Using DB gallery with images:', sortedImages.length);
          }
        } else {
          setError("Could not find the requested property");
        }
      } catch (err) {
        console.error("Error fetching data:", err);
        setError(err.message || "An error occurred while loading the data");
      } finally {
        setLoading(false);
      }
    };
    
    if (itemId && itemType) {
      fetchData();
    }
  }, [itemId, itemType, getProperty, getGroupProperty, cities, communities]);

  // Set up drag and drop for file uploads
  useEffect(() => {
    console.log('Setting up drag and drop handlers');
    const dropArea = dropAreaRef.current;
    if (!dropArea) {
      console.warn('Drop area ref is null!');
      return;
    }

    const handleDragOver = (e) => {
      e.preventDefault();
      e.stopPropagation();
      console.log('Drag over event detected');
      dropArea.classList.add('drag-over');
    };

    const handleDragLeave = (e) => {
      e.preventDefault();
      e.stopPropagation();
      console.log('Drag leave event detected');
      dropArea.classList.remove('drag-over');
    };

    const handleDrop = (e) => {
      e.preventDefault();
      e.stopPropagation();
      console.log('Drop event detected');
      dropArea.classList.remove('drag-over');
      
      if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
        console.log('Files dropped:', e.dataTransfer.files.length);
        const imageFiles = Array.from(e.dataTransfer.files).filter(file => 
          file.type.startsWith('image/'));
        
        console.log('Image files filtered:', imageFiles.length);
        if (imageFiles.length > 0) {
          setUploadFiles(imageFiles);
          toast.info(`📥 ${imageFiles.length} image${imageFiles.length > 1 ? 's' : ''} ready to upload`);
        } else {
          toast.warn("⚠️ Only image files are accepted");
        }
      }
    };

    // Add event listeners
    console.log('Adding event listeners to drop area');
    dropArea.addEventListener('dragover', handleDragOver);
    dropArea.addEventListener('dragleave', handleDragLeave);
    dropArea.addEventListener('drop', handleDrop);
    // Add dragenter for better drag detection
    dropArea.addEventListener('dragenter', (e) => {
      e.preventDefault();
      e.stopPropagation();
      console.log('Drag enter event detected');
    });

    return () => {
      // Clean up event listeners
      if (dropArea) {
        console.log('Removing event listeners');
        dropArea.removeEventListener('dragover', handleDragOver);
        dropArea.removeEventListener('dragleave', handleDragLeave);
        dropArea.removeEventListener('drop', handleDrop);
        dropArea.removeEventListener('dragenter', handleDragOver);
      }
    };
  }, []);

  const handleFileChange = (e) => {
    const files = Array.from(e.target.files).filter(file => file.type.startsWith('image/'));
    setUploadFiles(files);
    
    if (files.length > 0) {
      toast.info(`📥 ${files.length} image${files.length > 1 ? 's' : ''} selected`);
    }
  };

  const handleUpload = async () => {
    if (uploadFiles.length === 0) {
      console.warn('No upload files selected');
      return;
    }
    
    if (!property) {
      console.error('Property data is missing');
      setError("Property data is missing. Please refresh the page and try again.");
      return;
    }
    
    if (!cityPath) {
      console.error('City path is missing');
      setError("City path could not be determined. Please refresh the page and try again.");
      return;
    }
    
    console.log('Starting upload with:', {
      fileCount: uploadFiles.length,
      property: property.id,
      cityPath
    });
    
    setUploading(true);
    setError(null);
    
    try {
      // Upload images using the database approach
      for (const file of uploadFiles) {
        console.log(`Uploading file ${file.name} to property ${property.id} in city ${cityPath}`);
        await imageService.uploadGalleryImage(file, property.id, cityPath);
      }
      
      // Refresh the gallery images from the database
      console.log('Refreshing gallery images from database');
      const dbGalleryImages = await imageService.getGalleryImages(property.id);
      const sortedImages = [...dbGalleryImages].sort((a, b) => a.position - b.position);
      setGalleryImages(sortedImages);
      
      // Clear the file input
      setUploadFiles([]);
      if (fileInputRef.current) fileInputRef.current.value = "";
      toast.success("✅ Images uploaded successfully!");
      
    } catch (err) {
      console.error("Error uploading files:", err);
      setError(err.message || "Failed to upload files");
      toast.error("❌ Failed to upload images: " + (err.message || "Unknown error"));
    } finally {
      setUploading(false);
    }
  };

  const handleDeleteImage = async (image) => {
    if (!image || !window.confirm("🗑️ Are you sure you want to delete this image?")) {
      return;
    }
    
    try {
      // Delete image using the database approach
      await imageService.deleteGalleryImage(image.id);
      
      // Refresh the gallery images from the database
      const dbGalleryImages = await imageService.getGalleryImages(property.id);
      const sortedImages = [...dbGalleryImages].sort((a, b) => a.position - b.position);
      setGalleryImages(sortedImages);
      
      toast.success("🗑️ Image deleted successfully");
    } catch (err) {
      console.error("Error deleting image:", err);
      setError(err.message || "Failed to delete image");
      toast.error("❌ Failed to delete image: " + (err.message || "Unknown error"));
    }
  };

  const handleCancel = () => {
    // Navigate back to property editor
    navigate(`/admin/properties/${itemType}/${itemId}`);
  };

  // Helper function to generate a stable ID for drag and drop
  const generateStableId = (image) => {
    if (image.id) return `image-${image.id}`;
    if (image.path) return `path-${image.path.split('/').pop()}`;
    if (image.filename) return `file-${image.filename}`;
    return `img-${uuidv4()}`;
  };

  if (loading) {
    return (
      <div className="loading-container">
        <Loader isVisible={true} />
        <p><span role="img" aria-label="loading">⏳</span> Loading gallery data...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="error-container">
        <h2><span role="img" aria-label="error">❌</span> Error</h2>
        <p>{error}</p>
        <button onClick={handleCancel} className="button">
          <span role="img" aria-label="back">⬅️</span> Back to Property
        </button>
      </div>
    );
  }

  return (
    <div className="gallery-manager">
      <div className="gallery-header">
        <h2><span role="img" aria-label="gallery">🖼️</span> Gallery Manager: {property?.name}</h2>
        <button onClick={handleCancel} className="back-button">
          <span role="img" aria-label="back">⬅️</span> Back to Property
        </button>
        <p>Manage your property gallery images.</p>
      </div>
      
      <div className="gallery-uploader" ref={dropAreaRef}>
        <h4><span role="img" aria-label="camera">📷</span> Upload New Images</h4>
        <div className="drag-instructions">
          <p><span role="img" aria-label="inbox">📥</span> Drag and drop files here or use the button below</p>
        </div>
        <div className="file-inputs">
          <input
            type="file"
            multiple
            onChange={handleFileChange}
            ref={fileInputRef}
            style={{ display: 'none' }}
            accept="image/*"
            id="gallery-file-upload"
          />
          <button 
            className="select-images-btn" 
            onClick={() => fileInputRef.current.click()}
          >
            <span role="img" aria-label="folder">📁</span> Select Images
          </button>
          {uploadFiles.length > 0 && (
            <button 
              className="upload-images-btn" 
              onClick={handleUpload}
              disabled={uploading}
            >
              {uploading ? (
                <>
                  Uploading...
                </>
              ) : (
                <>
                  <span role="img" aria-label="upload">🔄</span> Upload 
                  {uploadFiles.length > 0 ? ` (${uploadFiles.length})` : ''}
                </>
              )}
            </button>
          )}
        </div>
      </div>
      
      <div className="gallery-images">
        <h4><span role="img" aria-label="photos">🖼️</span> Current Gallery Images</h4>
        
        {galleryImages.length === 0 ? (
          <div className="empty-gallery">
            <p><span role="img" aria-label="empty">📷</span> No images in the gallery yet. Upload some images to get started.</p>
          </div>
        ) : (
          <div className="gallery-grid">
            {galleryImages.map((image, index) => {
              const imageId = generateStableId(image);
              return (
                <div 
                  key={imageId}
                  className="gallery-item"
                >
                  <span className="image-position">{index + 1}</span>
                  <img
                    src={imageService.getImageUrl(image.path)}
                    alt={image.description || `Gallery ${index}`}
                    loading="lazy"
                  />
                  <div className="gallery-item-overlay">
                    <button 
                      className="delete-button"
                      onClick={() => handleDeleteImage(image)}
                      title="Delete image"
                    >
                      <span role="img" aria-label="delete">🗑️</span> Delete
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default GalleryManager;